import { useMemo, useState } from "react";
import { Button, SectionCard, useToast } from "@a2z/ui";
import { useTranslations, useLocale } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { pickLocalized } from "@/lib/i18n/localized";
import type {
  ProductOptionGroupInput,
  ProductVariantInput,
  ProductVariantView,
  ProductView,
} from "@/lib/api/types";
import { useAddVariantsMutation } from "@/modules/products/services/useVariantMutations";
import { BoxIcon } from "./product-edit-icons";
import {
  ProductAddVariantModal,
  type AddVariantSizeColor,
} from "./ProductAddVariantModal";
import { ProductVariantsEditorSheet } from "./ProductVariantsEditorSheet";
import {
  findColorGroup,
  findSizeGroup,
  swatchHex,
} from "@/modules/products/lib/variantsDisplay";

type Props = {
  storeSlug: string;
  product: ProductView;
  /** Currency label shown next to prices and inside the modals. */
  currency?: string;
};

const slug = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "") || "v";

function PlusIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

type FlatVariant = {
  id: string;
  label: string;
  swatch: string | null;
  price: number;
  discountedPrice: number | null;
  stockCount: number;
  isActive: boolean;
};

/**
 * Read-only variants table. Editing happens in dedicated sheets — "Edit stock"
 * (stock only) and "Manage variants" (full) — so the table stays a calm
 * overview. "Add variant" opens the add flow.
 */
export function ProductVariantsCard({
  storeSlug,
  product,
  currency = "EGP",
}: Props) {
  const { mutateAsync: addVariants, isPending } = useAddVariantsMutation();
  const toast = useToast();
  const t = useTranslations();
  const locale = useLocale();
  const [modalOpen, setModalOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<"stock" | "full" | null>(null);

  const sizeGroup = findSizeGroup(product);
  const colorGroup = findColorGroup(product);

  const flat: FlatVariant[] = useMemo(
    () =>
      product.variants.map((v: ProductVariantView) => {
        const label =
          v.selections
            .map((s) => pickLocalized(locale, s.optionValueAr, s.optionValueEn))
            .filter(Boolean)
            .join(" · ") || t(keys.products.variantDefaultLabel);
        const colorSel = colorGroup
          ? v.selections.find((s) => s.optionGroupId === colorGroup.id)
          : undefined;
        const swatch = colorSel
          ? swatchHex({
              colorHex: colorSel.optionValueColorHex,
              valueEn: colorSel.optionValueEn,
              valueAr: colorSel.optionValueAr,
            })
          : null;
        return {
          id: v.id,
          label,
          swatch,
          price: v.price,
          discountedPrice: v.discountedPrice,
          stockCount: v.stockCount,
          isActive: v.isActive,
        };
      }),
    [product.variants, colorGroup, locale, t],
  );

  // The selection-less base default isn't a variant the seller made — it only
  // exists once they add options. So "has variants" is driven by option groups,
  // not the raw variant rows, and the base default is never shown as a row.
  const hasRealVariants = product.optionGroups.length > 0;
  const totalStock = flat.reduce((acc, v) => acc + v.stockCount, 0);
  const stockMax = Math.max(40, ...flat.map((v) => v.stockCount));
  const subtitle = hasRealVariants
    ? t(keys.products.variantsCardSubtitle, {
        count: flat.length,
        units: totalStock,
      })
    : undefined;

  const existingPairs = useMemo(
    () =>
      product.variants.map((v) => {
        const sizeSel = sizeGroup
          ? v.selections.find((s) => s.optionGroupId === sizeGroup.id)
          : undefined;
        const colorSel = colorGroup
          ? v.selections.find((s) => s.optionGroupId === colorGroup.id)
          : undefined;
        return {
          size: sizeSel ? sizeSel.optionValueEn || sizeSel.optionValueAr : "—",
          color: colorSel
            ? colorSel.optionValueEn || colorSel.optionValueAr
            : "—",
        };
      }),
    [product.variants, sizeGroup, colorGroup],
  );

  const defaultPrice = useMemo(() => {
    const found =
      product.variants.find((v) => v.id === product.defaultVariantId) ??
      product.variants.find((v) => v.isDefault) ??
      product.variants[0];
    return found?.price ?? 0;
  }, [product.variants, product.defaultVariantId]);

  const handleAdd = async (rows: AddVariantSizeColor[]) => {
    if (rows.length === 0) return;

    type GroupDraft = {
      key: string;
      nameAr: string;
      nameEn: string;
      values: {
        key: string;
        valueAr: string;
        valueEn: string;
        colorHex?: string;
      }[];
    };
    const cloneGroup = (
      g: typeof sizeGroup,
      fallbackAr: string,
      fallbackEn: string,
    ): GroupDraft =>
      g
        ? {
            key: g.id,
            nameAr: g.nameAr,
            nameEn: g.nameEn ?? fallbackEn,
            values: g.values.map((v) => ({
              key: v.id,
              valueAr: v.valueAr,
              valueEn: v.valueEn ?? "",
              colorHex: v.colorHex ?? undefined,
            })),
          }
        : {
            key: `g-${slug(fallbackEn)}-add`,
            nameAr: fallbackAr,
            nameEn: fallbackEn,
            values: [],
          };

    const sizeDraft = cloneGroup(sizeGroup, "المقاس", "Size");
    const colorDraft = cloneGroup(colorGroup, "اللون", "Color");

    // Find-or-add a value within a draft group, returning its key.
    const ensureValue = (
      draft: GroupDraft,
      ar: string,
      en: string,
      colorHex?: string,
    ): string => {
      const lower = (en || ar).trim().toLowerCase();
      const existing = draft.values.find(
        (v) =>
          v.valueEn.trim().toLowerCase() === lower ||
          v.valueAr.trim().toLowerCase() === lower,
      );
      if (existing) {
        if (colorHex && !existing.colorHex) existing.colorHex = colorHex;
        return existing.key;
      }
      const key = `v-${slug(en || ar)}-${draft.values.length}-add`;
      draft.values.push({ key, valueAr: ar, valueEn: en, colorHex });
      return key;
    };

    const newVariants: ProductVariantInput[] = rows.map((row, index) => {
      const sizeKey = ensureValue(sizeDraft, row.sizeAr || row.size, row.size);
      const colorKey = ensureValue(
        colorDraft,
        row.colorAr || row.color,
        row.color,
        row.hex,
      );
      return {
        key: `v-new-${index}-add`,
        price: row.price,
        discountedPrice: row.discountedPrice ?? null,
        cost: row.cost ?? null,
        stockCount: row.stockCount,
        forceOutOfStock: false,
        isActive: true,
        isDefault: false,
        selections: [
          { optionGroupKey: sizeDraft.key, optionValueKey: sizeKey },
          { optionGroupKey: colorDraft.key, optionValueKey: colorKey },
        ],
      };
    });

    const optionGroups: ProductOptionGroupInput[] = [
      {
        key: sizeDraft.key,
        nameAr: sizeDraft.nameAr,
        nameEn: sizeDraft.nameEn,
        sortOrder: 0,
        values: sizeDraft.values.map((v, i) => ({
          key: v.key,
          valueAr: v.valueAr,
          valueEn: v.valueEn,
          sortOrder: i,
        })),
      },
      {
        key: colorDraft.key,
        nameAr: colorDraft.nameAr,
        nameEn: colorDraft.nameEn,
        sortOrder: 1,
        values: colorDraft.values.map((v, i) => ({
          key: v.key,
          valueAr: v.valueAr,
          valueEn: v.valueEn,
          colorHex: v.colorHex ?? null,
          sortOrder: i,
        })),
      },
    ];

    await addVariants({
      storeSlug,
      productId: product.id,
      optionGroups,
      variants: newVariants,
    });
    toast.success(
      rows.length === 1
        ? t(keys.products.variantsAddVariant)
        : t(keys.products.addNVariants, { count: rows.length }),
    );
  };

  const productName =
    pickLocalized(locale, product.nameAr, product.nameEn) ||
    t(keys.products.thisProductFallback);

  return (
    <>
      <SectionCard
        id="options"
        variant="flat"
        className="max-lg:!rounded-[18px] max-lg:!border-[color:var(--hair)] max-lg:!bg-[color:var(--soft)] max-lg:[&>header]:!items-center max-lg:[&>header>span]:hidden max-lg:[&>header>div:first-of-type>span:nth-child(2)]:hidden"
        title={t(keys.products.variantsCardTitle)}
        subtitle={subtitle}
        icon={<BoxIcon />}
        right={
          <div className="flex flex-wrap items-center justify-end gap-1.5">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setEditorMode("full")}
              disabled={isPending || !hasRealVariants}
            >
              {t(keys.products.variantsManage)}
            </Button>
            <Button
              type="button"
              variant="link"
              size="sm"
              onClick={() => setModalOpen(true)}
              disabled={isPending}
              leadingIcon={<PlusIcon />}
            >
              {t(keys.products.variantsAddVariant)}
            </Button>
          </div>
        }
      >
        {!hasRealVariants ? (
          <p className="rounded-[14px] border border-[color:var(--border-light)] py-6 text-center text-[13px] text-[color:var(--text-muted)]">
            {t(keys.products.variantsCardEmpty)}
          </p>
        ) : (
          <div className="overflow-hidden rounded-[14px] border border-[color:var(--border-light)]">
            <div className="hidden grid-cols-[minmax(0,1fr)_90px_minmax(120px,160px)] gap-3 border-b border-[color:var(--border-light)] bg-[color:var(--surface-alt)] px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.04em] text-[color:var(--text-muted)] lg:grid">
              <span>{t(keys.products.variantsColVariant)}</span>
              <span className="text-end">{t(keys.common.price)}</span>
              <span>{t(keys.products.stockLabel)}</span>
            </div>

            {flat.map((v, i) => {
              const tone =
                v.stockCount === 0
                  ? "bg-[color:var(--error-500)]"
                  : v.stockCount < 10
                    ? "bg-[color:var(--accent-500)]"
                    : "bg-[color:var(--success-500)]";
              const pct = Math.min(100, (v.stockCount / stockMax) * 100);
              return (
                <div
                  key={v.id}
                  className={`flex flex-col gap-2.5 px-4 py-3.5 lg:grid lg:grid-cols-[minmax(0,1fr)_90px_minmax(120px,160px)] lg:items-center lg:gap-3 ${
                    i > 0 ? "border-t border-[color:var(--border-light)]" : ""
                  }`}
                >
                  <div className="flex min-w-0 items-center gap-2.5">
                    <span
                      aria-hidden="true"
                      className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-[color:var(--border-light)] bg-[color:var(--surface-alt)] text-[color:var(--text-muted)]"
                    >
                      {v.swatch ? (
                        <span
                          className="h-3 w-3 rounded-full border border-black/10"
                          style={{ background: v.swatch }}
                        />
                      ) : (
                        <PlusCircleIcon />
                      )}
                    </span>
                    <span className="truncate text-[13.5px] font-semibold text-[color:var(--text-primary)]">
                      {v.label}
                    </span>
                    {!v.isActive ? (
                      <span className="shrink-0 rounded-full bg-[color:var(--surface-alt)] px-1.5 py-0.5 text-[11px] text-[color:var(--text-muted)]">
                        {t(keys.products.statusHidden)}
                      </span>
                    ) : null}
                  </div>
                  <div className="flex items-center justify-between gap-1 text-[13.5px] text-[color:var(--text-secondary)] lg:justify-end lg:text-end">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.04em] text-[color:var(--text-muted)] lg:hidden">
                      {t(keys.common.price)}
                    </span>
                    <span className="flex items-center gap-1">
                      {v.discountedPrice != null &&
                      v.discountedPrice < v.price ? (
                        <>
                          <span className="text-[color:var(--text-muted)] line-through">
                            {v.price}
                          </span>
                          <span className="font-semibold text-[color:var(--accent-600)]">
                            {v.discountedPrice}
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="text-[11px] font-semibold text-[color:var(--text-muted)]">
                            {currency}
                          </span>
                          <span>{v.price}</span>
                        </>
                      )}
                    </span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.04em] text-[color:var(--text-muted)] lg:hidden">
                      {t(keys.products.stockLabel)}
                    </span>
                    <span className="w-[22px] shrink-0 text-[13.5px] font-semibold text-[color:var(--text-primary)]">
                      {v.stockCount}
                    </span>
                    <span className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-[color:var(--surface-alt)]">
                      <span
                        className={`absolute inset-y-0 start-0 rounded-full ${tone}`}
                        style={{ width: `${pct}%` }}
                      />
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>

      <ProductAddVariantModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        productName={productName}
        currency={currency}
        existing={existingPairs}
        defaultPrice={defaultPrice}
        onAdd={handleAdd}
      />

      {editorMode ? (
        <ProductVariantsEditorSheet
          isOpen
          mode={editorMode}
          onClose={() => setEditorMode(null)}
          storeSlug={storeSlug}
          product={product}
          currency={currency}
        />
      ) : null}
    </>
  );
}

function PlusCircleIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 8v8M8 12h8" />
    </svg>
  );
}
